/** @jsx jsx */
import { jsx, Button } from '@front10/base-elements';
import { Icon } from '../../../packages/base-elements/Icon/Icon';
import { FilterButton } from '../../lab/FilterButton';
import { FlexibleFilterContainer } from '../../lab/FlexibleFilterContainer';
import { Input } from '../../lab/Input';
import { Listbox } from '../../lab/Listbox';
import { SelectProps } from '../../lab/utils';
import { useTranslation } from '@front10/utils';
import { useState, useMemo, useEffect } from 'react';
import { Controller, UseFormMethods } from 'react-hook-form';
import { BookingComponentsConfigurations } from './bookingComponentsConfigurations';
import { Rule, Rules } from './Rules';
import { Panels } from './Panels';
import { CustomCheckbox } from './CustomCheckbox';

export interface FlightMoreInfo {
  promoCode?: string;
  carCompany?: string;
  hotel?: string;
  carType?: string;
  rules: Record<string, boolean>;
  hotelCategory?: string;
  age?: string;
  nationality?: string;
  excludeBasicEconomy?: boolean;
}

interface FlightMoreInfoProps {
  defaultPromoCode?: string;
  rules: Rule[];
  carTypes: SelectProps;
  ages: SelectProps;
  hotelCategories: SelectProps;
  nationalities: SelectProps;
  onChange?: (value: Partial<FlightMoreInfo>) => void;
  type: string;
  componentsConfigurations: BookingComponentsConfigurations;
  iconClear?: any;
  enabledDoneButton?: boolean;
  defaultExcludeBasicEconomy?: boolean;
}

export function getDefaultRules(rules?: Rule[]) {
  return rules ? Object.fromEntries(rules.map((rule) => [rule.name, rule.defaultValue])) : {};
}

export function AdvancedFilterSelector(
  props: FlightMoreInfoProps &
    Pick<UseFormMethods<any>, 'control' | 'register' | 'getValues' | 'setValue'>
) {
  const defaultValue: FlightMoreInfo = {
    promoCode: props.defaultPromoCode,
    carType: props.carTypes.defaultValue,
    rules: getDefaultRules(props.rules),
    hotelCategory: props.hotelCategories.defaultValue,
    age: props.ages.defaultValue,
    nationality: props.nationalities.defaultValue,
    excludeBasicEconomy: props.defaultExcludeBasicEconomy
  };
  const [savedState, setSavedState] = useState(defaultValue);
  const [savedStateOld, setSavedStateOld] = useState(savedState);
  const [activeDone, setActiveDone] = useState(true);
  const [dismiss, setDismiss] = useState(false);
  const translate = useTranslation();

  const regexMap: any = {
    onlyNumbers: '[0-9, ]',
    onlyLetters: '[a-zA-Z, ]',
    lettersAndNumbers: '[a-zA-Z, 0-9]'
  };

  const onKeyPress = (e: any) => {
    const maximumAmountCharacters =
      props.componentsConfigurations.hotelSelector?.maximumAmountCharacters;
    if (
      maximumAmountCharacters &&
      savedState.hotel &&
      savedState.hotel.length === maximumAmountCharacters
    ) {
      e.preventDefault();
    } else {
      const charactersAllowed = props.componentsConfigurations.hotelSelector?.allowedCharacters;
      if (charactersAllowed !== undefined) {
        if (charactersAllowed !== 'all') {
          const regexString = regexMap[charactersAllowed] || charactersAllowed;
          const regex = new RegExp(regexString);
          if (regex && !regex.test(e.key)) {
            e.preventDefault();
          }
        }
      }
    }
  };

  const onPaste = (event: any) => {
    const maximumAmountCharacters =
      props.componentsConfigurations.hotelSelector?.maximumAmountCharacters;
    if (
      maximumAmountCharacters &&
      event.clipboardData.getData('text').length > maximumAmountCharacters
    ) {
      event.preventDefault();
    } else {
      const charactersAllowed = props.componentsConfigurations.hotelSelector?.allowedCharacters;
      if (charactersAllowed !== undefined) {
        if (charactersAllowed !== 'all') {
          const value = event.clipboardData.getData('text');
          const regex = new RegExp(`^${regexMap[charactersAllowed] || charactersAllowed}+$`);
          if (!regex.test(value)) {
            event.preventDefault();
          }
        }
      }
    }
  };

  const register = props.register;
  const control = props.control;
  const showPromoCode = props.componentsConfigurations.promotionalCodeSelector?.enabled ?? true;
  const showExcludeBasicEconomy =
    props.componentsConfigurations.excludeBasicEconomy?.enabled ?? false;
  const hasHotel =
    props.type.includes('hotel') && (props.componentsConfigurations.hotelSelector?.enabled ?? true);
  const hasCar = props.type.includes('car');

  const handleReset = (name: string) => {
    setSavedState({ ...savedState, [name]: '' });
    setActiveDone(false);
  };
  const handleChange = (e: any) => {
    const name = e.target.name;
    const value = e.target.value;
    setSavedState({ ...savedState, [name]: value });
    setActiveDone(false);
  };
  const { getValues } = props;

  const activeFilter = useMemo(() => {
    let active = false;
    const state = getValues([
      'promoCode',
      'carCompany',
      'hotel',
      'rules',
      'carType',
      'hotelCategory',
      'age',
      'nationality',
      'excludeBasicEconomy'
    ]);

    let key: keyof typeof state;

    for (key in state) {
      active = !!(Boolean(state[key]) || active);
    }
    return active;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getValues(), dismiss]);

  useEffect(() => {
    if (activeFilter) {
      setActiveDone(false);
      setDismiss(false);
    } else if (dismiss) {
      setActiveDone(true);
      setDismiss(false);
    }
  }, [activeFilter, dismiss]);

  return (
    <FlexibleFilterContainer
      title={translate('cmp-booking-title-filter', 'Filters')}
      label={translate('cmp-flight-advanced-title', 'Advanced')}
      enabledDoneButton={props.enabledDoneButton}
      labelBottomBtn={translate('cmp-booking-mask-advanced-done-button', 'Done')}
      disabledDoneButton={activeDone}
      saveOnClose
      toggleElement={
        <FilterButton
          active={activeFilter}
          value={translate('cmp-flight-advanced-title', 'Advanced')}
        />
      }
      onSave={() => {
        const state = props.getValues([
          'promoCode',
          'carCompany',
          'hotel',
          'rules',
          'carType',
          'hotelCategory',
          'age',
          'nationality',
          'excludeBasicEconomy'
        ]);
        setSavedStateOld(state);
        setSavedState(state);
        if (!activeFilter) {
          setActiveDone(true);
        }
      }}
      onDismiss={() => {
        Object.keys(savedStateOld).forEach((key) => {
          props.setValue(key, (savedStateOld as any)[key]);
        });
        const state = props.getValues([
          'promoCode',
          'carCompany',
          'hotel',
          'rules',
          'carType',
          'hotelCategory',
          'age',
          'nationality',
          'excludeBasicEconomy'
        ]);
        setSavedState(state);
        setDismiss(true);
      }}
      isOpenVisible={true}
    >
      <Panels>
        {showPromoCode ? (
          <Input
            ref={register}
            name="promoCode"
            onChange={handleChange}
            defaultValue={savedState.promoCode}
            value={savedState.promoCode}
            active={Boolean(savedState.promoCode)}
            label={translate('cmp-flight-promo-code-label', 'Promo code')}
            placeholder={translate('cmp-flight-promo-code-placeholder', 'Type your code')}
            layout="standard"
            rightElement={
              savedState.promoCode ? (
                <Button
                  variant="icon"
                  onClick={() => {
                    handleReset('promoCode');
                  }}
                  type="button"
                  aria-label={translate('cmp-booking-mask-hotel-name-clear-button', 'Clear')}
                >
                  <Icon
                    icon={props.iconClear ?? 'close'}
                    size={18}
                    sx={{ stroke: 'textSoft', strokeWidth: '60px' }}
                  />
                </Button>
              ) : null
            }
          />
        ) : null}
        {hasHotel ? (
          <Input
            name="hotel"
            label={translate('cmp-booking-mask-hotel-name-label', 'Hotel Name')}
            placeholder={translate('cmp-booking-mask-hotel-name-placeholder', 'Type de hotel name')}
            layout="standard"
            defaultValue={savedState.hotel}
            onKeyPress={onKeyPress}
            onPaste={onPaste}
            ref={register}
            value={savedState.hotel}
            active={Boolean(savedState.hotel)}
            onChange={handleChange}
            rightElement={
              savedState.hotel ? (
                <Button
                  variant="icon"
                  onClick={() => {
                    handleReset('hotel');
                  }}
                  type="button"
                  aria-label={translate('cmp-booking-mask-hotel-name-clear-button', 'Clear')}
                >
                  <Icon
                    icon={props.iconClear ?? 'close'}
                    size={18}
                    sx={{ stroke: 'textSoft', strokeWidth: '60px' }}
                  />
                </Button>
              ) : null
            }
          />
        ) : null}
        {hasHotel && props.hotelCategories.options.length ? (
          <Controller
            as={({ onChange }) => (
              <Listbox
                layout="standard"
                label={translate('cmp-booking-hotel-category-label', 'Category')}
                options={props.hotelCategories.options}
                defaultValue={savedState.hotelCategory}
                onChange={(newValue) => {
                  setSavedState({ ...savedState, hotelCategory: newValue });
                  onChange(newValue);
                }}
              />
            )}
            name="hotelCategory"
            control={control}
            defaultValue={savedState.hotelCategory}
          />
        ) : null}
        {props.type === 'hotel' && props.nationalities.options.length ? (
          <Controller
            as={({ onChange }) => (
              <Listbox
                layout="standard"
                label={translate('cmp-booking-hotel-category-label', 'Nationality')}
                options={props.nationalities.options}
                defaultValue={savedState.nationality}
                onChange={(newValue) => {
                  setSavedState({ ...savedState, nationality: newValue });
                  onChange(newValue);
                }}
              />
            )}
            name="nationality"
            control={control}
            defaultValue={savedState.nationality}
          />
        ) : null}
        {hasCar ? (
          <Input
            name="carCompany"
            label={translate('cmp-booking-mask-car-rental-company-label', 'Car rental company')}
            defaultValue={savedState.carCompany}
            value={savedState.carCompany}
            active={Boolean(savedState.carCompany)}
            onChange={handleChange}
            placeholder={translate(
              'cmp-booking-mask-car-rental-company-placeholder',
              'Type car rental company'
            )}
            layout="standard"
            ref={register}
            rightElement={
              savedState.carCompany ? (
                <Button
                  variant="icon"
                  onClick={() => {
                    handleReset('carCompany');
                  }}
                  type="button"
                  aria-label={translate('cmp-booking-mask-hotel-name-clear-button', 'Clear')}
                >
                  <Icon
                    icon={props.iconClear ?? 'close'}
                    size={18}
                    sx={{ stroke: 'textSoft', strokeWidth: '60px' }}
                  />
                </Button>
              ) : null
            }
          />
        ) : null}
        {props.type === 'car' && props.ages.options.length ? (
          <Controller
            name="age"
            control={control}
            render={({ onChange }) => (
              <Listbox
                label={translate('cmp-booking-mask-car-age-label', 'Driver age')}
                layout="standard"
                defaultValue={savedState.age}
                options={props.ages.options}
                onChange={(newValue) => {
                  setSavedState({ ...savedState, age: newValue });
                  props?.setValue('age', newValue);
                }}
              />
            )}
          />
        ) : null}
        {props.rules.length ? (
          <Controller
            name="rules"
            control={control}
            as={<Rules options={props.rules} defaultValue={savedState.rules} gap={2} />}
            defaultValue={savedState.rules}
          />
        ) : null}
        {showExcludeBasicEconomy ? (
          <Controller
            name="excludeBasicEconomy"
            control={control}
            render={({ onChange }) => (
              <CustomCheckbox
                name="excludeBasicEconomy"
                label={translate(
                  'cmp-booking-mask-exclude-basic-economy',
                  'Show Basic Economy Fares'
                )}
                defaultValue={
                  savedState.excludeBasicEconomy === undefined
                    ? false
                    : savedState.excludeBasicEconomy
                }
                onChange={(e: any) => {
                  onChange(e);
                  setSavedState({ ...savedState, excludeBasicEconomy: e });
                  setActiveDone(false);
                }}
                gap={2}
              />
            )}
            defaultValue={savedState.excludeBasicEconomy}
          />
        ) : null}
      </Panels>
    </FlexibleFilterContainer>
  );
}
